import React from "react";
import styled from "styled-components";
import { PrimaryButton, SecondaryButton } from "../common/Button";
import { Kicker } from "../common/Section";
import releases, { Release } from "../../../data/releases";

const getMetaYear = (meta?: string) => {
  const match = meta?.match(/\b(19|20)\d{2}\b/);
  return match ? Number(match[0]) : 0;
};

const heroFeature =
  releases.find((r) => r.id === "peru_remix") ??
  [...releases].sort(
    (a, b) => getMetaYear(b.meta) - getMetaYear(a.meta)
  )[0];

/* ── styled ── */

const Section = styled.section`
  position: relative;
  padding: clamp(7rem, 12vw, 10rem) 8vw 5rem;
  @media (max-width: 480px) { padding-left: 5vw; padding-right: 5vw; }
  background:
    radial-gradient(circle at top right, rgba(214,179,106,.18), transparent 28%),
    radial-gradient(circle at 18% 22%, var(--ink-08), transparent 22%),
    linear-gradient(180deg, rgba(11,11,13,.92), rgba(5,5,6,1));
  &::after {
    content: "";
    position: absolute;
    inset: auto 8vw 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--ink-12), transparent);
  }
`;

const Grid = styled.div`
  position: relative; z-index: 2;
  display: grid;
  grid-template-columns: minmax(0,1.35fr) minmax(280px,0.85fr);
  gap: clamp(2rem,5vw,5rem);
  align-items: end;
  @media (max-width: 1024px) { grid-template-columns: 1fr; }
`;

const Content = styled.div`position: relative; z-index: 2;`;

const Title = styled.h1`
  max-width: 14ch;
  font-family: "DM Serif Display", serif;
  font-size: clamp(3.8rem,8vw,7rem);
  line-height: .92;
  letter-spacing: -.04em;
  margin-bottom: 24px;
  color: var(--ink);
`;

const Lead = styled.p`
  max-width: 42rem;
  font-size: clamp(1.02rem,1.5vw,1.18rem);
  line-height: 1.85;
  color: rgba(243,239,230,.76);
  margin-bottom: 34px;
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 14px; max-width: 760px; margin-bottom: 34px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const MetaBlock = styled.div`
  padding-top: 16px;
  border-top: 1px solid var(--ink-12);
  strong { display: block; margin-bottom: 8px; font-size: 13px; letter-spacing: .16em; text-transform: uppercase; color: var(--ink); }
  p { color: var(--muted); line-height: 1.65; font-size: 14px; }
`;

const Actions = styled.div`
  display: flex; flex-wrap: wrap; gap: 14px;
  @media (max-width: 480px) { flex-direction: column; }
`;

const Side = styled.div`
  display: grid; gap: 20px; align-self: stretch;
  @media (max-width: 1024px) { gap: 24px; }
`;

const FeatureCard = styled.div`
  position: relative; overflow: hidden;
  border: 1px solid var(--ink-12);
  background: linear-gradient(180deg, rgba(255,255,255,.05), rgba(255,255,255,.02));
  box-shadow: 0 24px 70px rgba(0,0,0,.28);
`;

const FeatureArt = styled.div`
  aspect-ratio: 1/1;
  background: rgba(255,255,255,.03);
  img { width: 100%; height: 100%; object-fit: contain; padding: 16px; }
`;

const FeatureBody = styled.div`
  padding: 20px 22px 22px;
  span { display: block; margin-bottom: 10px; font-size: 11px; letter-spacing: .16em; text-transform: uppercase; color: var(--accent-2); }
  h2 { font-family: "DM Serif Display", serif; font-size: clamp(1.8rem,2.4vw,2.5rem); line-height: .98; margin-bottom: 10px; color: var(--ink); }
  p { color: var(--muted); line-height: 1.7; font-size: 14px; }
`;

const Stats = styled.div`
  display: flex; flex-direction: column; gap: 18px;
  padding-left: 24px;
  border-left: 1px solid rgba(214,179,106,.3);
  @media (max-width: 1024px) {
    flex-direction: row; flex-wrap: wrap;
    border-left: none; border-top: 1px solid rgba(214,179,106,.3);
    padding-left: 0; padding-top: 24px; gap: 28px;
  }
  @media (max-width: 400px) { flex-direction: column; gap: 18px; }
`;

const StatItem = styled.div`
  strong { display: block; font-family: "DM Serif Display", serif; font-size: clamp(2rem,3vw,2.8rem); color: var(--accent); line-height: 1; margin-bottom: 6px; }
  span { font-size: 11px; letter-spacing: .16em; text-transform: uppercase; color: var(--muted); }
`;

/* ── component ── */

const HomeHero: React.FC = () => (
  <Section>
    <Grid>
      <Content>
        <Kicker>Music Publishing · Artist Services · Global Strategy</Kicker>
        <Title>We publish records that move culture.</Title>
        <Lead>
          RMS is an Atlanta-based music publishing and artist services company
          for globally scalable records. We develop original compositions, manage
          rights, and build release strategy for artists, managers, labels, and
          brands — from Atlanta to every market that matters.
        </Lead>
        <MetaRow>
          <MetaBlock>
            <strong>For artists &amp; teams</strong>
            <p>Release planning, collaboration strategy, and publishing-minded execution.</p>
          </MetaBlock>
          <MetaBlock>
            <strong>For labels &amp; partners</strong>
            <p>Creative development, sync-ready compositions, and cross-market alignment.</p>
          </MetaBlock>
          <MetaBlock>
            <strong>For catalogs</strong>
            <p>Rights clarity, long-tail value, and records built to last beyond a moment.</p>
          </MetaBlock>
        </MetaRow>
        <Actions>
          <PrimaryButton to="/contact">Start an inquiry</PrimaryButton>
          <SecondaryButton to="/music">View catalog</SecondaryButton>
        </Actions>
      </Content>

      <Side>
        <FeatureCard>
          <FeatureArt>
            <img src={heroFeature.image} alt={heroFeature.title} loading="lazy" />
          </FeatureArt>
          <FeatureBody>
            <span>Featured release</span>
            <h2>{heroFeature.title}</h2>
            <p>
              {heroFeature.artist && `${heroFeature.artist} · `}
              {heroFeature.meta} · One of the strongest crossover records in the
              catalog.
            </p>
          </FeatureBody>
        </FeatureCard>

        <Stats>
          <StatItem>
            <strong>1B+</strong>
            <span>Catalog streams across major DSPs</span>
          </StatItem>
          <StatItem>
            <strong>10+</strong>
            <span>Countries via label partnerships</span>
          </StatItem>
          <StatItem>
            <strong>FIFA</strong>
            <span>Music consultant for FIFA Sound</span>
          </StatItem>
        </Stats>
      </Side>
    </Grid>
  </Section>
);

export default HomeHero;
